import L from 'leaflet'

/**
 * Vehicle icon images mapped to vehicle_type.
 * All images are top-down views facing UP (0° = north).
 * The icon is rotated by CSS transform based on the device heading.
 */
const VEHICLE_IMAGES: Record<string, string> = {
  car: '/vehicles/car_up_forward.png',
  truck: '/vehicles/truck.webp',
  van: '/vehicles/van.avif',
  motorcycle: '/vehicles/motorcycle.png',
}

// Fallback marker for unknown vehicle types
const DEFAULT_MARKER_URL = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png'

const ICON_SIZE = 40

/**
 * Creates a Leaflet DivIcon for a vehicle that rotates based on heading.
 *
 * @param vehicleType - Device vehicle type (car, truck, van, motorcycle, etc.)
 * @param heading     - GPS heading in degrees (0=north, 90=east, 180=south, 270=west)
 * @param isMoving    - Whether the device is currently moving (speed > 0)
 * @returns A Leaflet DivIcon with the vehicle image rotated to match heading
 */
export function createVehicleIcon(
  vehicleType?: string | null,
  heading?: number | null,
  isMoving?: boolean,
): L.DivIcon | L.Icon {
  const imageUrl = vehicleType ? VEHICLE_IMAGES[vehicleType] : null

  if (!imageUrl) {
    // No vehicle image available — use default Leaflet marker (no rotation)
    return new L.Icon({
      iconUrl: DEFAULT_MARKER_URL,
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    })
  }

  const rotation = heading ?? 0
  const opacity = isMoving === false ? 0.6 : 1.0

  return new L.DivIcon({
    className: 'vehicle-marker',
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: [ICON_SIZE / 2, ICON_SIZE / 2],
    popupAnchor: [0, -ICON_SIZE / 2],
    html: `<div style="
      width: ${ICON_SIZE}px;
      height: ${ICON_SIZE}px;
      transform: rotate(${rotation}deg);
      transition: transform 0.5s ease;
      opacity: ${opacity};
    ">
      <img
        src="${imageUrl}"
        style="
          width: 100%;
          height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        "
        alt="${vehicleType}"
      />
    </div>`,
  })
}

/**
 * List of vehicle types that have custom icons.
 */
export const VEHICLE_TYPES_WITH_ICONS = Object.keys(VEHICLE_IMAGES)
